import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { ArrowLeft } from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { getEvent, getLeague } from "../../graphql/queries";
import { updateEvent } from "../../graphql/mutations";
import { formatDateForInput } from "../../utils/dateUtils";
import {
  getLeagueFormats,
  getLeagueGameLabel,
} from "../../lib/leagueGames";

const client = generateClient();

const EditEvent: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: "",
    format: "",
    date: "",
    location: "",
    description: "",
    maxRounds: 4,
    isCompleted: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch event data
  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  const { data: league } = useQuery({
    queryKey: ["league", event?.leagueId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getLeague,
        variables: { id: event!.leagueId },
      });
      return result.data.getLeague;
    },
    enabled: !!event?.leagueId,
  });

  const gameFormats = getLeagueFormats(league?.game);
  const formats =
    formData.format && !gameFormats.includes(formData.format)
      ? [formData.format, ...gameFormats]
      : gameFormats;

  // Update mutation
  const updateEventMutation = useMutation({
    mutationFn: async (updatedEvent: any) => {
      const result = await client.graphql({
        query: updateEvent,
        variables: { input: updatedEvent },
      });
      return result.data.updateEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      navigate(`/admin/events/${eventId}`);
    },
    onError: (error: any) => {
      console.error("Error updating event:", error);
      setError(error.message || "Failed to update event");
    },
  });

  // Load event data into form
  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name || "",
        format: event.format || "",
        date: formatDateForInput(event.date),
        location: event.location || "",
        description: event.description || "",
        maxRounds: event.maxRounds || 4,
        isCompleted: event.isCompleted || false,
      });
    }
  }, [event]);

  const handleInputChange = (
    field: string,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (!eventId) {
        throw new Error("Event ID is required");
      }

      const updatedEventData = {
        id: eventId,
        name: formData.name,
        format: formData.format,
        date: formData.date,
        location: formData.location,
        description: formData.description,
        maxRounds: formData.maxRounds,
        isCompleted: formData.isCompleted,
      };

      await updateEventMutation.mutateAsync(updatedEventData);
    } catch (err: any) {
      console.error("Error updating event:", err);
      setError(err.message || "Failed to update event");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (eventLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading event: {eventError.message}
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">Event not found</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/events/${eventId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Event
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Edit Event</h1>
          <p className="text-muted-foreground mt-2">
            Update{" "}
            {league ? getLeagueGameLabel(league.game) : "event"} details and
            settings
          </p>
        </div>
      </div>

      <div className="max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>Event Details</CardTitle>
            <CardDescription>
              Update the details for this tournament event
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {/* Event Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Event Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Standard Showdown, Modern Masters"
                  required
                />
              </div>

              {/* Format */}
              <div className="space-y-2">
                <Label htmlFor="format">Format *</Label>
                <Select
                  value={formData.format}
                  onValueChange={(value) => handleInputChange("format", value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    {formats.map((format) => (
                      <SelectItem key={format} value={format}>
                        {format}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  required
                />
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) =>
                    handleInputChange("location", e.target.value)
                  }
                  placeholder="e.g., Game Store A, 123 Main St"
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Event description, prize support, etc."
                  rows={3}
                />
              </div>

              {/* Max Rounds */}
              <div className="space-y-2">
                <Label htmlFor="maxRounds">Max Rounds</Label>
                <Input
                  id="maxRounds"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.maxRounds}
                  onChange={(e) =>
                    handleInputChange("maxRounds", parseInt(e.target.value))
                  }
                />
              </div>

              {/* Completion Status */}
              <div className="space-y-2">
                <Label
                  htmlFor="isCompleted"
                  className="flex items-center gap-2"
                >
                  <input
                    id="isCompleted"
                    type="checkbox"
                    checked={formData.isCompleted}
                    onChange={(e) =>
                      handleInputChange("isCompleted", e.target.checked)
                    }
                    className="rounded border-gray-300"
                  />
                  Mark as Completed
                </Label>
                <p className="text-sm text-muted-foreground">
                  Check this box if the event has finished
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/admin/events/${eventId}`)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Updating..." : "Update Event"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditEvent;
